import React from "react";
import {
    Store,
    Building2,
    Stethoscope,
    Factory,
    Handshake,
    ArrowRight,
} from "lucide-react";
import { FadeUpGroup } from "../components/FadeUpGroup";

const INDUSTRIES = [
    {
        key: "retail",
        name: "Retail",
        icon: Store,
        bestFor: "Multi-store retailers managing sales, stock, and people across formats.",
        products: ["Mativex", "Vyaahar", "Karya", "Attestra"],
        uses: [
            "Daily store-level sales and category mix dashboards",
            "Inventory ageing, transfers, and reorder automation",
            "Customer feedback and repeat-purchase cohorts",
            "Roster, attendance, and payroll for store staff",
            "Vendor onboarding and PO sign-off in minutes",
        ],
    },
    {
        key: "real-estate",
        name: "Real Estate",
        icon: Building2,
        bestFor: "Developers, brokerages, and asset managers tracking projects and capital.",
        products: ["Vexen", "Acrium", "Mativex", "Vyaahar", "Attestra"],
        uses: [
            "Bookings, collections, and absorption analytics",
            "Project-level cost, milestone, and TAT tracking",
            "Tower-by-tower committed cost vs. budget (Acrium)",
            "Buyer agreement and demand letter automation",
            "Vendor and contractor payment workflows",
            "Investor and lender reporting packs",
        ],
    },
    {
        key: "healthcare",
        name: "Healthcare",
        icon: Stethoscope,
        bestFor: "Hospitals, clinics, and diagnostics groups managing revenue and people.",
        products: ["Mativex", "Vexen", "Karya", "Vyaahar"],
        uses: [
            "Revenue cycle visibility — billing to collection",
            "Insurance denial and rejection tracking",
            "Provider, OPD, and OT productivity",
            "Roster, leave, and credential management",
            "Audit-ready financials and statutory filings",
        ],
    },
    {
        key: "manufacturing",
        name: "Manufacturing",
        icon: Factory,
        bestFor: "Plants and contract manufacturers tracking output and unit economics.",
        products: ["Vexen", "Mativex", "Vyaahar", "Karya"],
        bestFit: ["Vexen", "Acrium"],
        uses: [
            "Production output and machine-level utilization",
            "Procurement intelligence and vendor scorecards",
            "Cost variance and yield reporting",
            "Worker attendance, wages, and statutory compliance",
            "Tax-compliant invoicing and reconciliation",
        ],
    },
    {
        key: "services",
        name: "Services",
        icon: Handshake,
        bestFor: "Professional service firms and agencies that bill on time and people.",
        products: ["Mativex", "Karya", "Vyaahar", "Attestra"],
        uses: [
            "Sales pipeline, conversion, and forecast",
            "Utilization, billable hours, and project margin",
            "Receivables and ageing tracker",
            "Contract and SoW signing workflows",
            "Employee onboarding and skills tracking",
        ],
    },
];

export const IndustriesPage = () => {
    return (
        <section
            className="section"
            id="industries"
            data-testid="industries-page"
        >
            <div className="container">
                <div className="section-head">
                    <span className="eyebrow">Industries</span>
                    <h1 className="h1" style={{ marginTop: 18 }}>
                        Solutions calibrated to your industry
                    </h1>
                    <p>
                        Pre-built dashboards, workflows, and playbooks for the
                        businesses we work with every day.
                    </p>
                </div>

                <FadeUpGroup>
                    <div
                        className="industry-grid"
                        style={{ gridTemplateColumns: "" }}
                    >
                        {INDUSTRIES.map((ind) => (
                            <div
                                key={ind.key}
                                className="industry-card fade-up"
                                data-testid={`industries-card-${ind.key}`}
                                style={{ padding: 36 }}
                            >
                                <div className="head-row">
                                    <span className="icon-square">
                                        <ind.icon size={24} />
                                    </span>
                                    <div>
                                        <h3 className="h3">{ind.name}</h3>
                                        <span
                                            style={{
                                                color: "var(--text-muted)",
                                                fontSize: "0.82rem",
                                            }}
                                        >
                                            Best for
                                        </span>
                                    </div>
                                </div>
                                <p className="muted" style={{ marginTop: 0 }}>
                                    {ind.bestFor}
                                </p>

                                <div
                                    style={{
                                        marginTop: 14,
                                        fontSize: "0.78rem",
                                        color: "var(--text-muted)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.1em",
                                    }}
                                >
                                    Recommended products
                                </div>
                                <div className="industry-chips">
                                    {ind.products.map((p) => (
                                        <span
                                            key={p}
                                            className="chip"
                                            style={{
                                                color: "var(--accent)",
                                                borderColor:
                                                    "var(--border-strong)",
                                            }}
                                        >
                                            {p}
                                        </span>
                                    ))}
                                </div>

                                <div
                                    style={{
                                        marginTop: 12,
                                        fontSize: "0.78rem",
                                        color: "var(--text-muted)",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.1em",
                                        marginBottom: 10,
                                    }}
                                >
                                    Use cases
                                </div>
                                <ul>
                                    {ind.uses.map((u) => (
                                        <li key={u}>{u}</li>
                                    ))}
                                </ul>

                                <a
                                    href={`/industries/${ind.key}`}
                                    className="link-arrow"
                                    style={{ marginTop: 22, display: "inline-flex" }}
                                    data-testid={`industries-explore-${ind.key}`}
                                >
                                    Explore {ind.name} <ArrowRight size={16} />
                                </a>
                            </div>
                        ))}
                    </div>
                </FadeUpGroup>
            </div>
        </section>
    );
};

export default IndustriesPage;
